import { useState } from "react";
import { useDeleteDomain, useDomains } from "../../api/queries";
import type { Domain } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { QueryStates } from "../../components/QueryStates";
import { DomainStatusPill } from "../../components/StatePill";
import { useToast } from "../../toast/ToastContext";
import { BindDomainModal } from "./BindDomainModal";
import { DeleteDomainDialog } from "./DeleteDomainDialog";
import "./services.css";

interface DomainsSectionProps {
  projectId: string;
  serviceId: string;
  serviceName: string;
  canManage: boolean;
}

/**
 * Hostnames bound to this service (ADR-0026 §3/§5, ADR-0027). Binding a
 * hostname now routes it: the server pushes a routing manifest to the
 * service's nodes on bind/unbind (#290) and Kelpie's embedded edge proxy
 * serves it once DNS points at the node and its ACME certificate issues.
 *
 * Each row shows the HONEST, node-reported routing/cert status (#303) via
 * {@link DomainStatusPill}: "Active" once it is serving, "Issuing certificate…"
 * while ACME is in flight, "HTTP only" on an ACME-disabled node, and "Awaiting
 * status" when no node has reported yet. The status is read from the server's
 * real per-node report (`Domain.status`), never inferred from a heuristic — a
 * hostname whose status is unknown shows "Awaiting status", not a fabricated
 * "active" (S-15). This closes the Founder-persona TTFU gap: bind → watch it
 * actually start serving.
 */
export function DomainsSection({ projectId, serviceId, serviceName, canManage }: DomainsSectionProps) {
  const { pushToast } = useToast();
  const {
    data: domainsPage,
    isPending,
    isError,
    error,
    refetch,
  } = useDomains(projectId, serviceId, { limit: 100 });
  const deleteMutation = useDeleteDomain(projectId, serviceId);

  const [bindOpen, setBindOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Domain | null>(null);

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const domainId = deleteTarget.id;
    const hostname = deleteTarget.hostname;
    deleteMutation.mutate(
      { domainId },
      {
        onSuccess: () => {
          pushToast({ title: `Unbound "${hostname}".`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't unbind "${hostname}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const bindButton = (
    <Button variant="primary" onClick={() => setBindOpen(true)}>
      Bind domain…
    </Button>
  );

  const domainItems = domainsPage?.items ?? [];

  return (
    <section className="service-detail__section" aria-labelledby="service-domains-heading">
      <div className="service-detail__section-header">
        <h2 id="service-domains-heading">Domains</h2>
        {canManage && domainItems.length > 0 && bindButton}
      </div>

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading domains…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load domains. Check your connection and try again."
        }
        isEmpty={domainItems.length === 0}
        empty={
          <EmptyState
            title="No domains bound"
            description="Bind a hostname to reach this service."
            action={canManage ? bindButton : undefined}
          />
        }
      >
        <ul className="connections-list">
          {domainItems.map((domain) => (
            <li className="connections-list__row" key={domain.id}>
              <div className="connections-list__row-top">
                <span className="connections-list__label">
                  {domain.hostname} <span aria-hidden="true">→</span> :{domain.targetPort}
                </span>
                <div className="connections-list__meta">
                  <DomainStatusPill status={domain.status} />
                  {canManage && (
                    <Button variant="secondary" onClick={() => setDeleteTarget(domain)}>
                      Unbind
                    </Button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="form-field__hint">
          Point an A or CNAME record at this machine's address for each bound hostname; it starts serving once DNS
          resolves and the certificate issues. Each row shows its live routing and certificate status above.
        </p>
      </QueryStates>

      {canManage && (
        <BindDomainModal
          open={bindOpen}
          projectId={projectId}
          serviceId={serviceId}
          serviceName={serviceName}
          onClose={() => setBindOpen(false)}
        />
      )}

      {canManage && (
        <DeleteDomainDialog
          domain={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </section>
  );
}
